import {
    Autocomplete,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FieldMapper from "../FieldMapper";
import DataPicker from "../DataPicker";
import { FieldMapping, ScopeOption } from "../fluss-builder-types";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import { FlussNodePanelData } from "./fluss-node-panel-data";
import {
    collectErrorCodeSuggestions,
    definitionsFor,
    errorEndCodeFor,
    metadataToScopeOptions,
    panelNodeKindFor,
    parseMapping,
    primaryBindingFor,
    resolveAnchorTable,
    sanitizeErrorCode,
    serializeMapping,
    updateBinding,
    updateErrorEndCode,
} from "./fluss-node-panel-utils";
import { useAnchorFinalStatuses, useFlussTableColumns } from "./useFlussNodePanelMetadata";
import { resolveDisplayText, translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { RecordSourceBindingField } from "./RecordSourceField";

interface EndPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    data: FlussNodePanelData;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

// ACH-046 (design_prim_pilot_045.md): single component used by BOTH the terminate_end
// ("Final status") and the plain end ("Named final status") panels, so they are physically
// incapable of diverging again. The options are the anchor table's REAL "status" column values
// (Fluss::Api::MetadataController#anchor_final_statuses) -- never a hardcoded universal list
// (the old hardcoded four-value list bug, ACH-046). Every branch below that can't offer real options
// disables the field with an explanation instead of silently falling back to fixed values.
// Opening the panel NEVER calls onChange on its own -- a legacy finalStatus that isn't in the
// resolved options is rendered as an extra "out of list" choice instead of being erased.
interface FinalStatusFieldProps {
    label: ReactNode;
    value: string;
    onChange: (next: string) => void;
    readOnly: boolean;
    tableName: string;
    tableLabel: string;
}

const FinalStatusField = ({ label, value, onChange, readOnly, tableName, tableLabel }: FinalStatusFieldProps) => {
    const { t } = useTranslation();
    const statusesQuery = useAnchorFinalStatuses(tableName);
    const data = statusesQuery.data;
    const keepStatusLabel = t("fluss.node_panel.end.keep_status", { defaultValue: "Keep current status" });

    const disabledSelect = (hint: ReactNode) => (
        <FlussNodePanelField label={label} hint={hint}>
            <TextField select size="small" value={value} disabled inputProps={{ "aria-label": String(label) }}>
                <MenuItem value="">{keepStatusLabel}</MenuItem>
                {value && (
                    <MenuItem value={value}>{value}</MenuItem>
                )}
            </TextField>
        </FlussNodePanelField>
    );

    if (!tableName || !tableLabel) {
        return disabledSelect(t("fluss.node_panel.end.status_choose_table", {
            defaultValue: "Selecione uma tabela de origem para definir o status final.",
        }));
    }

    if (statusesQuery.isLoading) {
        return disabledSelect(t("fluss.node_panel.end.status_loading", {
            defaultValue: "Loading the anchor table's status options...",
        }));
    }

    if (statusesQuery.isError || !data) {
        return disabledSelect(t("fluss.node_panel.end.status_unresolved", {
            defaultValue: "Could not resolve the anchor table \"{{table}}\" -- select a table first.",
            table: tableLabel,
        }));
    }

    if (!data.column) {
        return disabledSelect(t("fluss.node_panel.end.status_no_column", {
            defaultValue: "{{table}} does not have a final status field.",
            table: tableLabel,
        }));
    }

    if (data.column_type?.toUpperCase() !== "CHOICE") {
        return (
            <FlussNodePanelField
                label={label}
                hint={t("fluss.node_panel.end.status_free_text", {
                    defaultValue: "This value is written as-is; it is not validated against a list.",
                })}
            >
                <TextField
                    size="small"
                    value={value}
                    disabled={readOnly}
                    onChange={(event) => onChange(event.target.value)}
                    inputProps={{ "aria-label": String(label) }}
                />
            </FlussNodePanelField>
        );
    }

    if (data.options.length === 0) {
        return disabledSelect(t("fluss.node_panel.end.status_no_values", {
            defaultValue: "No final status values are available for {{table}}.",
            table: tableLabel,
        }));
    }

    const isLegacyValue = Boolean(value) && !data.options.some((option) => option.value === value);

    return (
        <FlussNodePanelField label={label}>
            <TextField
                select
                size="small"
                value={value}
                disabled={readOnly}
                onChange={(event) => onChange(event.target.value)}
                inputProps={{ "aria-label": String(label) }}
            >
                <MenuItem value="">{keepStatusLabel}</MenuItem>
                {isLegacyValue && (
                    <MenuItem value={value}>
                        {t("fluss.node_panel.end.status_legacy_value", {
                            defaultValue: "{{value}} (not in the current list)",
                            value,
                        })}
                    </MenuItem>
                )}
                {data.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {resolveDisplayText(t, option.label)}
                    </MenuItem>
                ))}
            </TextField>
        </FlussNodePanelField>
    );
};

// C6: the End declares the process outputs as `name <- reference` (a JSON object persisted in
// processOutputs). The caller receives them as <Call Activity>.<name>.
const OUTPUT_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

const ProcessOutputsField = ({ value, readOnly, onChange }: {
    value: FieldMapping[];
    readOnly: boolean;
    onChange: (rows: FieldMapping[]) => void;
}) => {
    const { t } = useTranslation();
    const nameLabel = String(t("fluss.node_panel.end.process_output_name", { defaultValue: "Output name" }));
    const update = (index: number, patch: Partial<FieldMapping>) =>
        onChange(value.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    return (
        <Stack spacing={1} data-testid="process-outputs-field">
            {value.map((row, index) => {
                const invalid = Boolean(row.target) && !OUTPUT_NAME.test(row.target);
                const duplicate = Boolean(row.target) && value.filter((candidate) => candidate.target === row.target).length > 1;
                return (
                    <Stack key={index} spacing={0.75} data-testid="process-output-row">
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <TextField
                                size="small"
                                fullWidth
                                value={row.target}
                                disabled={readOnly}
                                error={invalid || duplicate}
                                inputProps={{ "aria-label": nameLabel }}
                                onChange={(event) => update(index, { target: event.target.value })}
                            />
                            <Typography aria-hidden="true" color="text.secondary" fontWeight={700}>←</Typography>
                            <IconButton
                                size="small"
                                color="error"
                                disabled={readOnly}
                                aria-label={String(t("fluss.builder.field_mapper.remove", { defaultValue: "Remove field" }))}
                                onClick={() => onChange(value.filter((_, rowIndex) => rowIndex !== index))}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <DataPicker
                            value={row.source}
                            onChange={(source) => update(index, { source })}
                            disabled={readOnly}
                            ariaLabel={String(t("fluss.builder.field_mapper.source", { defaultValue: "Source" }))}
                        />
                    </Stack>
                );
            })}
            <Button
                size="small"
                disabled={readOnly}
                startIcon={<AddIcon />}
                onClick={() => onChange([...value, { target: "", source: undefined }])}
            >
                {t("fluss.builder.field_mapper.add", { defaultValue: "Field" })}
            </Button>
        </Stack>
    );
};

export const EndNodePanel = (props: EndPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, data, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);
    const binding = primaryBindingFor(selection.businessObject);
    const bindingKind = kind === "terminate_end" ? "terminate" : kind;
    // PRIM-017/018: `table` here is the anchor, not an independent destination — a binding
    // without an explicit table means "inherit the process anchor", never "task".
    const [tableName, setTableName] = useState(String(binding?.table || resolveAnchorTable(modeler) || ""));
    const tableLabel = data.tables.find((table) => table.name === tableName)?.label;
    const displayTableLabel = tableLabel
        ? String(t(tableLabel, { defaultValue: tableLabel }))
        : String(t("fluss.node_panel.table_unknown", { defaultValue: "Selected table" }));
    const [finalStatus, setFinalStatus] = useState(String(binding?.finalStatus || ""));
    const [notify, setNotify] = useState(String(binding?.notify || "false") === "true");
    const [templateId, setTemplateId] = useState(String(binding?.templateId || ""));
    const [updates, setUpdates] = useState<FieldMapping[]>(parseMapping(binding?.recordUpdates));
    const [milestoneName, setMilestoneName] = useState(String(binding?.name || ""));
    const [milestoneField, setMilestoneField] = useState(String(binding?.field || ""));
    const [milestoneValue, setMilestoneValue] = useState(String(binding?.valueSource || ""));
    const [processOutputs, setProcessOutputs] = useState<FieldMapping[]>(parseMapping(binding?.processOutputs));
    const columnsQuery = useFlussTableColumns(tableName, data.tables);
    const recordFields = useMemo(
        () => metadataToScopeOptions(columnsQuery.data || []),
        [columnsQuery.data]
    );
    useEffect(() => {
        const current = primaryBindingFor(selection.businessObject);
        setTableName(String(current?.table || resolveAnchorTable(modeler) || ""));
        setFinalStatus(String(current?.finalStatus || ""));
        setNotify(String(current?.notify || "false") === "true");
        setTemplateId(String(current?.templateId || ""));
        setUpdates(parseMapping(current?.recordUpdates));
        setMilestoneName(String(current?.name || ""));
        setMilestoneField(String(current?.field || ""));
        setMilestoneValue(String(current?.valueSource || ""));
        setProcessOutputs(parseMapping(current?.processOutputs));
    }, [selection.id, selection.businessObject, modeler]);

    // PRIM-017/018 agravante: this used to always write `table: tableName` back into the
    // binding on every edit, even though there is no UI here to choose a table explicitly — so
    // the resolved anchor (or the "task" fallback) got permanently baked into the model on the
    // first edit of any End/Timer node. Never persist it; a binding without `table` keeps
    // inheriting the anchor, which is the correct behavior.
    const change = (updates: Record<string, unknown>) => {
        updateBinding(selection, modeler, bindingKind, updates);
        onChanged();
    };

    let body;
    if (kind === "terminate_end") {
        body = (
            <Stack spacing={1.25}>
                <FinalStatusField
                    label={t("fluss.node_panel.end.final_status", { defaultValue: "Final status" })}
                    value={finalStatus}
                    tableName={tableName}
                    tableLabel={displayTableLabel}
                    readOnly={readOnly}
                    onChange={(next) => {
                        setFinalStatus(next);
                        change({ finalStatus: next });
                    }}
                />
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={notify}
                            disabled={readOnly}
                            onChange={(event) => {
                                setNotify(event.target.checked);
                                change({
                                    notify: event.target.checked ? "true" : "false",
                                    templateId: event.target.checked ? templateId : "",
                                });
                            }}
                        />
                    )}
                    label={t("fluss.node_panel.terminate.notify", {
                        defaultValue: "Notify owners of cancelled tasks",
                    })}
                />
                {notify && (
                    <FlussNodePanelField
                        label={t("fluss.node_panel.terminate.template", {
                            defaultValue: "Notification template",
                        })}
                    >
                        <Autocomplete
                            size="small"
                            options={data.templates}
                            disabled={readOnly}
                            value={data.templates.find((template) => template.id === templateId) || null}
                            getOptionLabel={(option) => option.label || option.name || option.code || ""}
                            isOptionEqualToValue={(option, current) => option.id === current.id}
                            onChange={(_, option) => {
                                const next = option?.id || "";
                                setTemplateId(next);
                                change({ notify: "true", templateId: next });
                            }}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </FlussNodePanelField>
                )}
            </Stack>
        );
    } else if (kind === "milestone") {
        const selectedField = recordFields.find((field) => field.name === milestoneField);
        body = (
            <Stack spacing={1.25}>
                <FlussNodePanelField
                    label={t("fluss.node_panel.milestone.name", { defaultValue: "Milestone name" })}
                >
                    <TextField
                        size="small"
                        value={milestoneName}
                        disabled={readOnly}
                        onChange={(event) => {
                            setMilestoneName(event.target.value);
                            change({ name: event.target.value });
                        }}
                    />
                </FlussNodePanelField>
                <FlussNodePanelField
                    label={t("fluss.node_panel.milestone.update_field", {
                        defaultValue: "Update field",
                    })}
                    fieldPath="valueSource"
                >
                    <TextField
                        select
                        size="small"
                        value={milestoneField.replace(/^record\./, "")}
                        disabled={readOnly}
                        onChange={(event) => {
                            const next = event.target.value;
                            setMilestoneField(next);
                            setMilestoneValue("");
                            change({ field: next, valueSource: "" });
                        }}
                    >
                        <MenuItem value="">
                            {t("fluss.node_panel.optional", { defaultValue: "Optional" })}
                        </MenuItem>
                        {recordFields.map((field) => (
                            <MenuItem key={field.name} value={field.name}>
                                {translateMetadataLabel(t, field) || field.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {milestoneField && (
                        <DataPicker
                            value={milestoneValue || undefined}
                            onChange={(next) => {
                                // PRIM-020: undefined means "cleared". PRIM-022 (round 6): a
                                // ValueField-backed literal (REFERENCE/CHOICE) can also emit an
                                // explicit `null` (e.g. its own clear button) -- valueSource is a
                                // single plain string here with no separate null/unmapped states,
                                // so both collapse to "" rather than stringifying to "undefined"
                                // or "null".
                                const serialized = next === undefined || next === null ? "" : String(next);
                                setMilestoneValue(serialized);
                                change({ field: milestoneField, valueSource: serialized });
                            }}
                            expectedType={selectedField?.type}
                            destinationField={selectedField}
                            disabled={readOnly}
                        />
                    )}
                </FlussNodePanelField>
            </Stack>
        );
    } else {
        body = (
            <Stack spacing={1.25}>
                <RecordSourceBindingField selection={selection} modeler={modeler} kind={bindingKind} readOnly={readOnly} onChanged={onChanged}
                    onResolved={(info) => setTableName(info.referencedTable || "")}
                    onSelected={(value, info) => setTableName(value ? info?.referencedTable || "" : resolveAnchorTable(modeler) || "")} />
                <FlussNodePanelField
                    label={t("fluss.node_panel.end.record_updates", {
                        defaultValue: "On completion, update selected record",
                    })}
                    fieldPath="recordUpdates"
                >
                    {/* PRIM-015 (round 6, call site #3): this updates an existing record, which
                        already exists and already satisfied its own required columns at
                        creation — never force/lock them here. */}
                    <FieldMapper
                        value={updates}
                        targetFields={recordFields}
                        templateText
                        disabled={readOnly}
                        enforceRequired={false}
                        onChange={(next) => {
                            setUpdates(next);
                            change({ recordUpdates: serializeMapping(next) });
                        }}
                    />
                </FlussNodePanelField>
                <FinalStatusField
                    label={t("fluss.node_panel.end.named_status", {
                        defaultValue: "Named final status",
                    })}
                    value={finalStatus}
                    tableName={tableName}
                    tableLabel={displayTableLabel}
                    readOnly={readOnly}
                    onChange={(next) => {
                        setFinalStatus(next);
                        change({ finalStatus: next });
                    }}
                />
                <FlussNodePanelField
                    label={t("fluss.node_panel.end.process_outputs", {
                        defaultValue: "Process outputs",
                    })}
                    fieldPath="processOutputs" issuePrefix
                    hint={t("fluss.node_panel.end.process_outputs_hint", {
                        defaultValue:
                            "Variables this process officially exposes to whoever calls it via Call Activity.",
                    })}
                >
                    <ProcessOutputsField
                        value={processOutputs}
                        readOnly={readOnly}
                        onChange={(rows) => {
                            setProcessOutputs(rows);
                            change({ processOutputs: serializeMapping(rows) });
                        }}
                    />
                </FlussNodePanelField>
            </Stack>
        );
    }

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "completion",
                label: t("fluss.node_panel.tab.completion", { defaultValue: "Completion" }),
                content: body,
            }]}
        />
    );
};

// PRIM-PILOT-044 (ACH-048): the "throw" symmetric counterpart of BoundaryErrorNodePanel
// (EventNodePanels.tsx, the "capture" side, intentionally untouched -- see plan.md DECISÃO 5).
// Lives here (not EventNodePanels.tsx) so the production capture panel stays in a forbidden_scope
// file with zero blast radius from this change. Writes the errorCode into the native BPMN
// <bpmn:error>/errorRef structure via fluss-node-panel-utils, NOT into a fluss:Binding.
export const ErrorEndNodePanel = (props: EndPanelProps) => {
    const { t } = useTranslation();
    const { selection, modeler, canViewAdvanced, readOnly, onChanged } = props;
    const kind = panelNodeKindFor(selection);

    const [code, setCode] = useState(errorEndCodeFor(selection.businessObject));
    const [inputValue, setInputValue] = useState(code);

    useEffect(() => {
        const current = errorEndCodeFor(selection.businessObject);
        setCode(current);
        setInputValue(current);
    }, [selection.id, selection.businessObject]);

    const definitions = useMemo(
        () => definitionsFor(selection.businessObject),
        [selection.businessObject]
    );
    const suggestions = useMemo(
        () => collectErrorCodeSuggestions(modeler, definitions),
        [modeler, definitions]
    );

    // DECISÃO 6/7 (plan.md): sanitize (strip commas, trim), no-op if unchanged from what's already
    // persisted, otherwise write through updateErrorEndCode (reuse-by-value or create) and notify.
    const commit = (next: string) => {
        const sanitized = sanitizeErrorCode(next);
        setInputValue(sanitized);
        if (sanitized === code) return;
        setCode(sanitized);
        updateErrorEndCode(selection, modeler, sanitized);
        onChanged();
    };

    let errorRef: any = selection.businessObject?.eventDefinitions?.[0]?.errorRef;
    if (errorRef && typeof errorRef === "string") {
        errorRef = (definitions?.rootElements || []).find(
            (candidate: any) => candidate?.$type === "bpmn:Error" && candidate.id === errorRef
        );
    }

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "error_end",
                label: t("fluss.node_panel.tab.error", { defaultValue: "Error" }),
                content: (
                    <Stack spacing={1.25}>
                        <FlussNodePanelField
                            label={t("fluss.node_panel.error_end.code", { defaultValue: "Error code to throw" })}
                        >
                            <Autocomplete
                                freeSolo
                                size="small"
                                disabled={readOnly}
                                options={suggestions}
                                inputValue={inputValue}
                                onInputChange={(_, value) => setInputValue(value)}
                                onChange={(_, value) => commit(String(value ?? ""))}
                                onBlur={() => commit(inputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={String(t("fluss.node_panel.error_end.code_placeholder", {
                                            defaultValue: "e.g. payment_failed",
                                        }))}
                                        inputProps={{
                                            ...params.inputProps,
                                            "aria-label": String(t("fluss.node_panel.error_end.code", {
                                                defaultValue: "Error code to throw",
                                            })),
                                        }}
                                    />
                                )}
                            />
                        </FlussNodePanelField>
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.error_end.hint", {
                                defaultValue:
                                    "Deixe em branco para falhar com o motivo genérico \"Fim com erro\". " +
                                    "Para ser capturado, o código precisa corresponder a um dos códigos " +
                                    "específicos configurados na borda de erro (sem distinguir maiúsculas de minúsculas).",
                            })}
                        </Typography>
                    </Stack>
                ),
            }]}
            advancedContent={(
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.error_end.reference", { defaultValue: "Referenced bpmn:Error" })}
                        {": "}
                        {String(errorRef?.id || "—")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t("fluss.node_panel.error_end.effective_code", { defaultValue: "Effective errorCode" })}
                        {": "}
                        {String(errorRef?.errorCode || errorRef?.id || "—")}
                    </Typography>
                </Stack>
            )}
        />
    );
};
